<template>
  <section ref="contentArea"
           class="user-select-none">
    <div ref="contentContainer"
         class="__content_container  d-flex align-items-center justify-content-center position-relative overflow-hidden"
         v-on:click="clickContainer($event)">
      <video ref="content" preload="none" class="h-100 flex-grow-1" />
      <div class="__fast_forward  position-relative w-40 text-light d-flex flex-column align-items-center justify-content-center p-2 position-absolute top-0 end-0 bottom-0"
           :class="{'opacity-0': !(this.clickCount > 1 && clickDirectionForward)}">
        <div ref="skipForward"
             class="position-absolute top-0 end-0 bottom-0 start-0"></div>
        <span class="fs-4"
              :class="{'invisible': this.clickCount <= 1}">
          {{this.clickCount <= 0 ? 0 : (this.clickCount - 1) * this.skipValue}}
        </span>
        <svgicon name="fast_forward" class="h-auto w-50" />
      </div>
      <div class="__rewind  position-relative w-40 text-light d-flex flex-column align-items-center justify-content-center p-2 position-absolute top-0 start-0 bottom-0"
           :class="{'opacity-0': !(this.clickCount > 1 && !clickDirectionForward)}">
        <div ref="skipBackward"
             class="position-absolute top-0 end-0 bottom-0 start-0"></div>
        <span class="fs-4"
              :class="{'invisible': this.clickCount <= 1}">
          {{this.clickCount <= 0 ? 0 : (this.clickCount - 1) * this.skipValue}}
        </span>
        <svgicon name="rewind" class="h-auto w-50" />
      </div>
    </div>
    <playback-control :duration="durationValue"
                      :currentTime="currentTimeValue"
                      :volume="volumeValue"
                      :muted="isMuted"
                      :played="mergedPlayed"
                      :buffered="convertedBuffered"
                      :status="status"
                      v-on:play="play"
                      v-on:pause="pause"
                      v-on:seekStart="seekStart"
                      v-on:seeking="seeking"
                      v-on:seekEnd="seekEnd"
                      v-on:volumeChanged="changeVolume" />
  </section>
</template>

<style scoped>
  .__fast_forward,
  .__rewind {
    box-shadow: 0 0 1rem 2rem var(--bs-dark);
    background-color: var(--bs-dark);
    opacity: 0.75;
    width: 25%;
    transition: 0.1s;
  }
</style>

<script>
  import { defineComponent } from "vue";
  import PlaybackControl from "components/control/playback_control";

  export default defineComponent({
    props: {
      src: {
        type: String,
        required: true,
      },
      currentTime: {
        type: Number,
        default: null,
      },
      muted: {
        type: Boolean,
        default: false,
      },
      volume: {
        type: Number,
        default: 1,
        validator: (val) => {
          return 0 <= val && val <= 1;
        },
      },
      alreadyPlayed: {
        type: Array,
        validator: (val) => {
          return val.every(i => Object.keys(i).includes("start") && Object.keys(i).includes("end"));
        }
      },
      seekbarColor: {
        type: String,
        default: "primary",
        validator: (val) => {
          return Object.keys(__COLOR_CODE).includes(val);
        },
      },
      skipValue: {
        type: Number,
        default: 5,
      }
    },
    computed: {
      mergedPlayed() {
        if (this.playedValue == null && this.alreadyPlayed == null) return null;
        let convertedPlayed = [];
        for (var i = 0; i < this.playedValue.length; i++) {
          convertedPlayed.push({
            start: this.playedValue.start(i),
            end: this.playedValue.end(i),
          });
        }
        if (convertedPlayed.length === 0) return this.alreadyPlayed;
        if (this.alreadyPlayed == null || this.alreadyPlayed.length === 0) return convertedPlayed;
        let mp = this.alreadyPlayed.concat(convertedPlayed);
        mp = mp.filter((x, xIndex) => !mp.some((y, yIndex) => xIndex !== yIndex && y.start <= x.start && x.end <= y.end));
        mp.forEach((x, xIndex) => {
          let t = mp.findIndex((y, yIndex) => xIndex !== yIndex && y.start <= x.start && x.start <= y.end);
          if (t >= 0) {
            mp[t].end = mp[xIndex].end;
            mp.splice(xIndex, 1);
          }
        });
        mp.forEach((x, xIndex) => {
          let t = mp.findIndex((y, yIndex) => xIndex !== yIndex && y.start <= x.end && x.end <= y.end);
          if (t >= 0) {
            mp[t].start = mp[xIndex].start;
            mp.splice(xIndex, 1);
          }
        });
        mp.sort((prevP, currentP) => prevP.start - currentP.start);
        return mp;
      },
      progress() {
        let val = this.mergedPlayed.reduce(
          (p, c) => p + (c.end - c.start),
          0
        );
        return Math.round(val / this.durationValue * 100);
      },
      convertedBuffered() {
        if (this.bufferedValue == null) return null;
        let convertedBuffered = [];
        for (var i = 0; i < this.bufferedValue.length; i++) {
          convertedBuffered.push({
            start: this.bufferedValue.start(i),
            end: this.bufferedValue.end(i),
          });
        }
        return convertedBuffered;
      },
    },
    data: () => {
      return {
        content: null,
        durationValue: 0,
        currentTimeValue: 0,
        volumeValue: 1,
        isMuted: false,
        status: "paused",
        pausedCurrent: true,
        started: false,
        playedValue: {},
        bufferedValue: {},
        inSeeking: false,
        timerId: null,
        clickCount: 0,
        clickDirectionForward: true,
        skipForward: null,
        skipBackward: null,
      }
    },
    methods: {
      loadContent() {
        this.$refs.content.src = this.src;
        this.$refs.content.addEventListener("loadedmetadata", function () {
          this.content = this.$refs.content;
          this.handlingEvents();
          this.durationValue = this.content.duration;
          this.content.volume = this.volume;
          this.volumeValue = this.volume;
          this.content.muted = this.muted;
          this.isMuted = this.muted;
          this.skipForward = this.$refs.skipForward;
          this.skipBackward = this.$refs.skipBackward;
        }.bind(this));
        this.$refs.content.load();
      },
      handlingEvents() {
        this.content.addEventListener("timeupdate", function () {
          this.currentTimeValue = this.content.currentTime;
          this.playedValue = this.content.played;
          this.bufferedValue = this.content.buffered;
        }.bind(this));
        this.content.addEventListener("progress", function () {
          this.bufferedValue = this.content.buffered;
        }.bind(this));
        this.content.addEventListener("ended", function () {
          this.status = "ended";
        }.bind(this));
        this.content.addEventListener("pause", function () {
          this.status = "paused";
        }.bind(this));
        this.content.addEventListener("playing", function () {
          if (!this.started) {
            this.started = true;
            this.$emit("start");
            console.log("start");
          }
          this.status = "playing";
        }.bind(this));
        this.content.addEventListener("volumechange", function () {
          this.volumeValue = this.content.volume;
        }.bind(this));
      },
      changeVolume(volumeParam) {
        this.volumeValue = volumeParam.volume;
        this.isMuted = volumeParam.muted;
        this.content.volume = this.volumeValue;
        this.content.muted = this.isMuted;
      },
      seekStart(currentTime) {
        this.inSeeking = true;
        this.pausedCurrent = this.content.paused;
        this.content.pause();
        if (this.status === "ended") this.status = "paused";
        this.content.currentTime = currentTime;
      },
      seeking(currentTime) {
        this.currentTimeValue = currentTime;
        this.content.currentTime = currentTime;
      },
      seekEnd() {
        this.inSeeking = false;
        this.content.currentTime = this.currentTimeValue;
        if (!this.pausedCurrent) this.content.play();
      },
      play() {
        if (this.content) this.content.play();
      },
      pause() {
        if (this.content) this.content.pause();
      },
      clickContainer(e) {
        if (this.content == null) return;
        if (this.timerId == null) {
          this.timerId = setTimeout(this.releaseTimer, 300);
          return;
        }

        clearTimeout(this.timerId);

        this.clickCount++;
        this.clickDirectionForward = e.target === this.skipForward ? true
          : e.target === this.skipBackward ? false
            : (e.offsetX >= (e.target.getBoundingClientRect().width / 2));

        if (this.clickCount >= 2) {
          if (this.status === "ended") this.status = "paused";
          if (this.content.paused === false) {
            this.pausedCurrent = this.content.paused;
            this.content.pause();
          }
          if (this.clickDirectionForward) {
            let ct = this.content.currentTime + ((this.clickCount - 1) * this.skipValue);
            this.currentTimeValue = ct > this.durationValue ? this.durationValue : ct;
          } else {
            let ct = this.content.currentTime - ((this.clickCount - 1) * this.skipValue);
            this.currentTimeValue = ct < 0 ? 0 : ct;
          }
        }

        this.timerId = setTimeout(this.releaseTimer, 300);
      },
      releaseTimer() {
        if (this.clickCount < 2) {
          this.clickCount = 0;
          return;
        }
        if (this.clickDirectionForward) {
          this.content.currentTime += ((this.clickCount - 1) * this.skipValue);
        } else {
          this.content.currentTime -= ((this.clickCount - 1) * this.skipValue);
        }
        if (!this.pausedCurrent) this.content.play();
        this.clickCount = 0;
      },
    },
    beforeCreate() {
    },
    created() {
    },
    mounted() {
      this.loadContent();
    },
    emits: ["start", "playing", "pause", "ended", "completed"],
    components: {
      "playback-control": PlaybackControl,
    },
    watch: {
      src: function () {
        this.content = null;
        this.loadContent();
      },
      progress: function () {
        if (this.progress === 100) {
          console.log("completed")
          this.$emit("completed");
        };
      },
    }
  });

</script>
